import {List, Map} from 'immutable'
import {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {createResource} from 'redux-json-api'
import {useForm, FormProvider} from 'react-hook-form'
import {Stack} from '@mui/material'

import Modal from '../../../../shared_components/Modal'
import {useNotificationContext} from '../../../../shared_components/notifications/NotificationContext'
import Select from '../../../../shared_components/forms/v3/Select'
import * as ButtonTypes from '../../../../shared_components/ButtonTypes'

import {isActiveAdvisorSeat} from '../../../../lib/proTools'
import {compareInt, addPossessive} from '../../../../lib/tools'
import FirmActions from '../../actions/FirmActions'


import './reassignClientsModal.scss'

export const ReassignClientsModal = props => {
  const [processing, setProcessing] = useState(false)
  const {alwaysNotify} = useNotificationContext()

  const methods = useForm({
    defaultValues: {
      advisor_seat_id: {value: ''}
    },
    mode: 'onSubmit'
  })

  // Filter active advisors from firmSeats
  const advisorItems = useMemo(() => {
    if (!props.firmSeats || props.firmSeats.isEmpty())
      return []

    return props.firmSeats
      .filter(seat => isActiveAdvisorSeat(seat) && !compareInt(seat.get('id'), props.userSeatId))
      .sortBy(seat => seat.get('name-from-user-model') || seat.get('name'))
      .map(seat => ({
        label: seat.get('name-from-user-model') || seat.get('name'),
        value: seat.get('id').toString()
      }))
      .toJS()
  }, [props.firmSeats, props.userSeatId])

  const handleSubmit = formData => {
    setProcessing(true)
    const payload = {
      type: 'client-reassignments',
      attributes: {
        'old-advisor-seat-id': parseInt(props.userSeatId, 10),
        'new-advisor-seat-id': parseInt(formData.advisor_seat_id.value, 10)
      }
    }

    props.createResource(payload)
      .then(() => {
        alwaysNotify.shortSuccess('Clients reassigned successfully.')
        props.closeModal()
        FirmActions.fetchFirmUser({firmId: props.firmId, firmUserSeatId: props.userSeatId})
      })
      .catch(error => {
        const errorMessage = error?.response?.data?.errors?.[0]?.title || "We're sorry, but something went wrong while reassigning clients. Please try again."
        alwaysNotify.shortError(errorMessage)
        setProcessing(false)
      })
  }

  const professionalDesignation = props.firmConfig?.professional_designation || props.firmConfig?.get('professional-designation') || 'advisor'

  return (
    <Modal visible={props.showModal} closer={props.closeModal}>
      <div className='reassign-clients-modal'>
        <h2>Reassign Clients</h2>
        <div className='reassign-clients-message'>
          Select a new {professionalDesignation.toLowerCase()} to reassign {addPossessive(props.user?.name || 'this user')} clients to:
        </div>
        <div className='form-wrapper'>
          <FormProvider {...methods}>
            <Stack component='form' spacing={2}>
              <Select
                name='advisor_seat_id'
                label={`Select a new ${professionalDesignation.toLowerCase()}:`}
                placeholder={`Select a new ${professionalDesignation.toLowerCase()}`}
                required
                errorMessage={`Please select a new ${professionalDesignation.toLowerCase()}.`}
                options={advisorItems}
                SelectProps={{
                  MenuProps: {
                    disablePortal: false,
                    sx: {zIndex: 9999}
                  }
                }}
              />
            </Stack>
          </FormProvider>
          <div className='button-group'>
            <ButtonTypes.Cancel onClick={props.closeModal} />
            <ButtonTypes.Continue
              loading={processing}
              onClick={methods.handleSubmit(handleSubmit)}
              disabled={advisorItems.length === 0}>
              Reassign Clients
            </ButtonTypes.Continue>
          </div>
        </div>
      </div>
    </Modal>
  )
}

ReassignClientsModal.defaultProps = {
  firmSeats: List()
}

ReassignClientsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  createResource: PropTypes.func.isRequired,
  firmConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Map)]),
  firmId: PropTypes.string,
  firmSeats: PropTypes.instanceOf(List),
  showModal: PropTypes.bool.isRequired,
  user: PropTypes.object,
  userSeatId: PropTypes.string.isRequired
}

export default connect(null, {createResource})(ReassignClientsModal)
